import { useId, type ReactNode } from 'react';
import { Icon, type IconName } from '../theme/Icon';
import { Button } from './Button';
import { ErrorBanner } from './ErrorBanner';
import { InfoTip } from './InfoTip';
import type { ApiError } from '@app/api/ApiError';
import s from './DataTable.module.css';
import { t } from '@app/i18n';

/** Página padrão das listas locais do contrato (`size` = 50). */
export const PAGE_SIZE = 50;

export type SortDirection = 'asc' | 'desc';

export interface SortState {
  /** `key` da coluna. */
  key: string;
  direction: SortDirection;
}

export interface DataTableColumn<T> {
  key: string;
  header: ReactNode;
  /** Célula. Recebe a linha e o índice na página. */
  cell: (row: T, index: number) => ReactNode;
  /** `true` = coluna ordenável (FR-128 — só as marcadas). */
  sortable?: boolean;
  /** Alinha à direita e usa numerais tabulares. */
  numeric?: boolean;
  /** Identificador técnico: célula em monoespaçada (FR-133). */
  mono?: boolean;
  /** Explicação ⓘ no cabeçalho (FR-132). */
  tip?: string;
  width?: string;
  /**
   * Cabeçalho só para leitor de tela (a coluna de ações, «Ações»). A tabela o envolve no `sr-only` e dá ao `th` a
   * classe `srOnlyHeader`, que segura o rótulo dentro do `.wrap` (o porquê está no `DataTable.module.css`).
   */
  srOnlyHeader?: boolean;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  /** Chave estável da linha. */
  rowKey: (row: T) => string;

  // ---- estados (FR-126) ----
  loading?: boolean;
  error?: ApiError | string | null;
  onRetry?: () => void;

  // ---- busca (FR-128) ----
  /** Passe `undefined` para esconder a busca. */
  search?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;

  // ---- filtros (FR-128) ----
  /** Comboboxes/chips de filtro, renderizados na barra. */
  filters?: ReactNode;
  /** Exibe "Limpar filtros" quando há algo aplicado. */
  onClearFilters?: () => void;
  filtersApplied?: boolean;

  // ---- ordenação (FR-128) ----
  sort?: SortState | null;
  onSortChange?: (sort: SortState) => void;

  // ---- paginação (FR-128: 50 por página, "n de m") ----
  page?: number;
  pageSize?: number;
  total?: number;
  onPageChange?: (page: number) => void;

  // ---- vazio ----
  emptyTitle?: string;
  emptyMessage?: ReactNode;
  emptyIcon?: IconName;
  /** Ação sugerida no estado vazio (ex.: "Importar do gateway"). */
  emptyAction?: ReactNode;

  /** Clique na linha (abre o drawer/detalhe). */
  onRowClick?: (row: T) => void;
  /** Rótulo acessível da tabela. */
  label: string;
  className?: string;
}

/**
 * Grid do produto (FR-126, FR-128): busca, filtros, ordenação, paginação de 50
 * com "n de m" e os três estados — carregando (esqueleto), erro com "Tentar de
 * novo" e vazio com mensagem própria da tela.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading,
  error,
  onRetry,
  search,
  onSearchChange,
  searchPlaceholder = t('common.ui.search'),
  filters,
  onClearFilters,
  filtersApplied,
  sort,
  onSortChange,
  page = 1,
  pageSize = PAGE_SIZE,
  total,
  onPageChange,
  emptyTitle = t('common.ui.emptyRecord'),
  emptyMessage,
  emptyIcon = 'list',
  emptyAction,
  onRowClick,
  label,
  className,
}: DataTableProps<T>) {
  const searchId = useId();
  const totalRows = total ?? rows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const first = totalRows === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, totalRows);

  const hasSearch = search !== undefined && onSearchChange !== undefined;
  const showToolbar = hasSearch || Boolean(filters) || Boolean(onClearFilters && filtersApplied);

  const toggleSort = (key: string) => {
    if (!onSortChange) return;
    const direction: SortDirection = sort?.key === key && sort.direction === 'asc' ? 'desc' : 'asc';
    onSortChange({ key, direction });
  };

  return (
    <div className={className}>
      {showToolbar ? (
        <div className={s.filters}>
          {hasSearch ? (
            <span className={s.fltin}>
              <Icon name="search" size="sm" aria-hidden="true" />
              <label className="sr-only" htmlFor={searchId}>
                {searchPlaceholder}
              </label>
              <input
                id={searchId}
                type="search"
                value={search}
                placeholder={searchPlaceholder}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </span>
          ) : null}
          {filters}
          {onClearFilters && filtersApplied ? (
            <button type="button" className={s.fltclear} onClick={onClearFilters}>
              <Icon name="close" size="xs" aria-hidden="true" />
              {t('common.ui.clearFilters')}
            </button>
          ) : null}
        </div>
      ) : null}

      {error ? (
        <div className={s.empty}>
          <ErrorBanner error={error} title={t('common.ui.loadFailed')} onRetry={onRetry} />
        </div>
      ) : (
        <>
          <div className={s.wrap}>
            <table className={s.table} aria-label={label} aria-busy={loading || undefined}>
              <thead>
                <tr>
                  {columns.map((col) => {
                    const isSorted = sort?.key === col.key;
                    const header = col.srOnlyHeader ? <span className="sr-only">{col.header}</span> : col.header;
                    return (
                      <th
                        key={col.key}
                        className={
                          [col.numeric ? s.num : '', col.srOnlyHeader ? s.srOnlyHeader : '']
                            .filter(Boolean)
                            .join(' ') || undefined
                        }
                        style={col.width ? { width: col.width } : undefined}
                        aria-sort={
                          isSorted
                            ? sort.direction === 'asc'
                              ? 'ascending'
                              : 'descending'
                            : col.sortable
                              ? 'none'
                              : undefined
                        }
                      >
                        {col.sortable && onSortChange ? (
                          <button
                            type="button"
                            className={s.sortBtn}
                            onClick={() => toggleSort(col.key)}
                          >
                            {header}
                            <Icon
                              name={
                                isSorted && sort.direction === 'desc' ? 'arrow-down' : 'chevron'
                              }
                              size="xs"
                              className={isSorted ? s.sorticOn : s.sortic}
                              aria-hidden="true"
                            />
                          </button>
                        ) : (
                          header
                        )}
                        {col.tip ? (
                          <InfoTip text={col.tip} label={t('common.ui.about.column')} size="xs" />
                        ) : null}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {loading
                  ? Array.from({ length: 5 }, (_, i) => (
                      <tr className={s.skelRow} key={`skel-${i}`}>
                        {columns.map((col) => (
                          <td key={col.key}>
                            <span className={s.skel} data-testid="datatable-skeleton" />
                          </td>
                        ))}
                      </tr>
                    ))
                  : rows.map((row, index) => (
                      <tr
                        key={rowKey(row)}
                        className={onRowClick ? s.clickable : undefined}
                        onClick={onRowClick ? () => onRowClick(row) : undefined}
                        // `row-<chave>`: o E2E afirma "a linha X ficou no estado Y"
                        // sem depender de ordem nem de paginação.
                        data-testid={`row-${rowKey(row)}`}
                      >
                        {columns.map((col) => (
                          <td
                            key={col.key}
                            className={[col.numeric ? s.num : '', col.mono ? s.mono : '']
                              .filter(Boolean)
                              .join(' ')}
                          >
                            {col.cell(row, index)}
                          </td>
                        ))}
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>

          {!loading && rows.length === 0 ? (
            <div className={s.empty}>
              <Icon name={emptyIcon} size="xl" className={s.emptyIcon} aria-hidden="true" />
              <div>
                <strong>{emptyTitle}</strong>
                {emptyMessage ? <p>{emptyMessage}</p> : null}
              </div>
              {emptyAction}
            </div>
          ) : null}

          {!loading && rows.length > 0 && onPageChange ? (
            <div className={s.pager}>
              <span className={s.pagerInfo}>
                {t('common.ui.page.range', { first, last, total: totalRows })}
              </span>
              <Button
                variant="out"
                size="sm"
                onClick={() => onPageChange(page - 1)}
                disabled={page <= 1}
              >
                {t('common.ui.page.previous')}
              </Button>
              <span className={[s.pgn, s.pgnOn].join(' ')} aria-current="page">
                {page}
              </span>
              <Button
                variant="out"
                size="sm"
                onClick={() => onPageChange(page + 1)}
                disabled={page >= totalPages}
              >
                {t('common.ui.page.next')}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
